import { DateTime } from "luxon"
import { getWeekStart } from "./buildMoonCalendar"
import { SYNODIC_MONTH, lastFullMoonBefore, moonPhase, nextFullMoonAfter, nextNewMoonAfter } from "./lunar"
import * as lunar from "./lunar"

function collectPhaseDates({ start, end, nextPhase, zone }) {
  const dates = []
  let cursor = new Date(start.toMillis() - 1)

  while (true) {
    const instant = nextPhase(cursor)
    if (instant.getTime() > end.toMillis()) break

    dates.push(DateTime.fromJSDate(instant, { zone }))
    cursor = new Date(instant.getTime() + 1)
  }

  return dates
}

function getWeekdayLabels(locale, weekStart) {
  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: "narrow",
    timeZone: "UTC",
  })

  // 2024-01-01 was a Monday, therefore its day-of-month matches the ISO
  // weekday number used by Luxon (Monday = 1 … Sunday = 7).
  return Array.from({ length: 7 }, (_, index) => {
    const isoWeekday = ((weekStart - 1 + index) % 7) + 1
    return formatter.format(new Date(Date.UTC(2024, 0, isoWeekday)))
  })
}

export default function buildMonthCalendar({ visibleMonth, today, selectedDate, locale = "en", weekStart = getWeekStart(locale), observationHour = 21 } = {}) {
  if (!visibleMonth?.isValid || !today?.isValid) {
    throw new TypeError("buildMonthCalendar requires valid Luxon visibleMonth and today values")
  }

  const zone = visibleMonth.zoneName
  const month = visibleMonth.setZone(zone).startOf("month")
  const currentDay = today.setZone(zone).startOf("day")
  const selection = selectedDate?.isValid ? selectedDate.setZone(zone).startOf("day") : null
  const lastFullMoonDate = DateTime.fromJSDate(lastFullMoonBefore(currentDay.endOf("day").toJSDate()), { zone }).startOf("day")
  const lastDay = month.endOf("month").startOf("day")
  const offsetFromWeekStart = (month.weekday - weekStart + 7) % 7
  const gridStart = month.minus({ days: offsetFromWeekStart })

  let gridEnd = lastDay.plus({ days: (weekStart + 6 - lastDay.weekday + 7) % 7 })
  const gridLength = Math.round(gridEnd.diff(gridStart, "days").days) + 1
  if (gridLength < 35) gridEnd = gridEnd.plus({ days: 7 })

  const phaseRange = {
    start: gridStart.startOf("day"),
    end: gridEnd.endOf("day"),
    zone,
  }
  const fullMoonDatesInGrid = collectPhaseDates({
    ...phaseRange,
    nextPhase: nextFullMoonAfter,
  })
  const newMoonDatesInGrid = collectPhaseDates({
    ...phaseRange,
    nextPhase: nextNewMoonAfter,
  })
  const fullMoonDays = new Set(fullMoonDatesInGrid.map(date => date.toISODate()))
  const newMoonDays = new Set(newMoonDatesInGrid.map(date => date.toISODate()))

  const weeks = []
  for (let weekStartDate = gridStart; weekStartDate <= gridEnd; weekStartDate = weekStartDate.plus({ days: 7 })) {
    const week = []

    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
      const date = weekStartDate.plus({ days: dayOffset })
      const iso = date.toISODate()
      const phase = moonPhase(date.set({ hour: observationHour }).toJSDate())

      // const phase = lunar.moonPhase(new Date(v))
      const offset = phase.age < lunar.SYNODIC_MONTH / 2
      let illu = phase.illumination
      illu = offset ? 2 - illu : illu
      // return illu

      week.push({
        date,
        iso,
        dayNumber: date.day,
        phase: {
          ...phase,
          progress: phase.age / SYNODIC_MONTH,
          illuprog: illu / 2,
        },
        // phase2: {
        //   ...phase,
        //   progress: phase.age / SYNODIC_MONTH,
        // },
        isCurrentMonth: date.hasSame(month, "month"),
        isToday: date.hasSame(currentDay, "day"),
        isSelected: selection ? date.hasSame(selection, "day") : false,
        isFullMoon: fullMoonDays.has(iso),
        isNewMoon: newMoonDays.has(iso),
      })
    }

    weeks.push(week)
  }

  return {
    month,
    weekdays: getWeekdayLabels(locale, weekStart),
    weekStart,
    weeks,
    latestFullMoonDate: lastFullMoonDate,
    daysSinceFullMoon: Math.max(0, Math.round(currentDay.diff(lastFullMoonDate, "days").days)),
    isFullMoonToday: currentDay.hasSame(lastFullMoonDate, "day"),
  }
}
